import { find, get, isEmpty, isNil, map } from 'lodash';
import { MARKET } from '@nandosaus/constants';
import { observer } from 'mobx-react';
import { RestaurantModel, useRootStore } from '@nandosaus/state-management';
import { restaurantPropTypes } from '@nandosaus/prop-types';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';

import { withAppProps } from '@utils/with-app-props';
import { ContentImageBlock } from '../../../components/content-image';
import { convertToSlug } from '../../../utils/convert-to-slug';
import { DeliveryOptionsBlock } from '../../../components/delivery-options-block';
import { fetch } from '../../../content/queries';
import { Layout } from '../../../components/layout';
import { logger } from '../../../utils/logger';
import { RestaurantDetailsTables } from '../../../components/restaurant-details-tables';
import { RestaurantJsonLd } from '../../../components/restaurant-jsonLd';
import { RestaurantOrderButton } from '../../../components/restaurant-order-button';
import { RestaurantPageHeader } from '../../../components/restaurant-page-header';
import { seoPropDefaults, seoPropTypes } from '../../../utils/extract-seo-meta-from-entry';
import { useMarket } from '../../../hooks/use-market';

const CONTENT_IMAGE = [
  {
    url: '/static/images/restaurant/catering.jpg',
    alt: 'Restaurant Catering Packages',
  },
];

const VIEW_BUTTON = {
  text: 'View our catering packages',
  href: 'https://catering.nandos.com.au/',
};

const RestaurantPage = ({ metaContent, restaurant }) => {
  const { RestaurantDetailsState, RestaurantStore } = useRootStore();

  const currentMarket = useMarket();

  const chosenRestaurant = useMemo(() => {
    if (RestaurantDetailsState.restaurant) {
      return RestaurantDetailsState.restaurant;
    }

    return RestaurantModel.create(restaurant);
  }, [RestaurantDetailsState.restaurant, restaurant]);

  const imageUrl = `https://assets.nandos.com.au/Nandos-${
    currentMarket === MARKET.AUSTRALIA ? 'Australia' : 'New-Zealand'
  }/images/Restaurant-Images/${chosenRestaurant.id}-restaurant-hero-header.png`;

  const headerImage = { url: imageUrl, alt: `Nandos ${chosenRestaurant.name} building image` };

  useEffect(() => {
    const updateDynamicRestaurantData = async () => {
      await RestaurantStore.loadRestaurants();

      const selected = find(RestaurantStore.asArray, ({ id }) => id === restaurant.id);

      if (selected) {
        RestaurantDetailsState.updateRestaurant(selected);
      } else {
        logger.error('Error updating dynamic restaurant data.', { restaurant });
      }
    };

    updateDynamicRestaurantData();
  }, [RestaurantDetailsState, RestaurantStore, restaurant, restaurant.id]);

  const restaurantTitle = useMemo(() => {
    if (isEmpty(chosenRestaurant)) {
      return null;
    }

    return `Nando’s | ${chosenRestaurant.seoTitleString} Restaurant`;
  }, [chosenRestaurant]);

  return (
    <Layout title={restaurantTitle} metaContent={metaContent}>
      <RestaurantJsonLd restaurant={restaurant} />
      <RestaurantPageHeader
        state={chosenRestaurant.address.state}
        name={chosenRestaurant.name}
        backgroundImage={headerImage}
      />

      <RestaurantOrderButton restaurant={chosenRestaurant} />

      <RestaurantDetailsTables restaurant={chosenRestaurant} />

      {!isEmpty(chosenRestaurant.formattedDeliveryServices) && !isNil(chosenRestaurant.orderingEnabled) && (
        <DeliveryOptionsBlock
          deliveryServices={chosenRestaurant.formattedDeliveryServices}
          orderingEnabled={chosenRestaurant.orderingEnabled}
        />
      )}
      {currentMarket === MARKET.AUSTRALIA && (
        <ContentImageBlock
          heading="Catering Packages"
          body="Need to feed the crew? Nando’s catering packages will spice up any event."
          image={CONTENT_IMAGE}
          button={VIEW_BUTTON}
        />
      )}
    </Layout>
  );
};
const getRestaurantMapping = async () => {
  const data = await fetch({
    query: gql`
      query getRestaurantNames {
        restaurants {
          id
          name
          address {
            state
          }
        }
      }
    `,
  });

  return get(data, 'restaurants', []);
};

export async function getStaticPaths() {
  const restaurantMapping = await getRestaurantMapping();

  return {
    paths: map(restaurantMapping, ({ name, address }) => ({
      params: { state: convertToSlug(address.state), restaurant: convertToSlug(name) },
    })),
    fallback: 'blocking',
  };
}
export const getStaticProps = withAppProps(async context => {
  const { restaurant: restaurantSlug } = context.params;

  const restaurantMapping = await getRestaurantMapping();
  const simpleRestaurant = find(restaurantMapping, restaurant => restaurantSlug === convertToSlug(restaurant.name));

  if (isEmpty(simpleRestaurant)) {
    return {
      notFound: true,
    };
  }

  const contentQuery = gql`
    query getStaticRestaurantDataById {
      restaurant(id: "${simpleRestaurant.id}") {
        id
        name
        longitude
        latitude
        phone
        address {
          address1
          address2
          country
          postcode
          state
          suburb
        }
        catering
        facilities
        deliveryServices {
          uberEats {
            live
            link
          }
          menulog {
            live
            link
          }
          doorDash {
            live
            link
          }
        }
        utcOffset
        seoTitle
        openingHours {
          day
          openingTime
          closingTime
        }
      }
    }
  `;

  const data = await fetch({
    query: contentQuery,
  });

  const restaurant = get(data, 'restaurant');

  if (isEmpty(restaurant)) {
    logger.error('Error fetching restaurant data while building static page.', {
      restaurantSlug,
      id: simpleRestaurant.id,
      restaurant,
    });
    return {
      notFound: true,
    };
  }

  const additionalMetaTags = [];
  if (!isEmpty(restaurant)) {
    additionalMetaTags.push({
      key: 'description',
      data: {
        name: 'description',
        content: `Contact details, location, store hours, and directions to Nando’s ${restaurant.seoTitleString} Restaurant. Visit us in-store or order online!`,
      },
    });
  }

  const metaContent = { additionalMetaTags };

  return {
    props: { metaContent, restaurant },
  };
});

RestaurantPage.propTypes = {
  metaContent: PropTypes.shape(seoPropTypes),
  restaurant: restaurantPropTypes.isRequired,
};

RestaurantPage.defaultProps = {
  metaContent: seoPropDefaults,
};

export default observer(RestaurantPage);
